import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { getProfile } from '../../services/profileService';

interface SignatureProps {
  permitId?: number | null;
  initialConfirmed?: boolean;
  onChange?: (values: any) => void;
}

interface Profile {
  id: number;
  name: string;
  designation: string;
  profile_image?: string;
}

const Signature = forwardRef<any, SignatureProps>(({ permitId, initialConfirmed = false, onChange }, ref) => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(initialConfirmed);
  const [error, setError] = useState('');
  const confirmationRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);

        const data = await getProfile();

        if (data) {
          setProfile({
            id: data.id,
            name: data.name || '',
            designation: data.user_type?.title || data.department?.name || 'Responsible Person',
            profile_image: data.profile_photo || '',
          });
        }
      } catch (err) {
        console.error('Failed to load profile', err);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  useEffect(() => {
    onChange?.({
      permit_id: permitId,
      responsible_person_id: profile?.id || null,
      name: profile?.name || '',
      designation: profile?.designation || '',
      confirmed,
    });
  }, [confirmed, onChange, permitId, profile]);

  useImperativeHandle(ref, () => ({
    validateForm: async () => {
      if (!confirmed) {
        setError('Please confirm the application before continuing.');
        requestAnimationFrame(() => {
          confirmationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          confirmationRef.current?.focus({ preventScroll: true });
        });
        return false;
      }

      setError('');
      return true;
    },

    getValues: () => ({
      permit_id: permitId,
      responsible_person_id: profile?.id || null,
      name: profile?.name || '',
      designation: profile?.designation || '',
      confirmed,
    }),
  }));

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <span className="text-gray-500">Loading profile...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8 px-2">
      {/* Responsible Person */}

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Responsible Person</h3>

        {profile && (
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
              {profile?.profile_image ? (
                <img src={profile.profile_image} alt={profile.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-gray-600 font-semibold">{profile?.name?.charAt(0)?.toUpperCase()}</span>
              )}
            </div>

            <div>
              <div className="font-medium text-gray-900">{profile.name}</div>

              <div className="text-sm text-gray-500">{profile.designation || 'Responsible Person'}</div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation */}

      <div className="border-t pt-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            ref={confirmationRef}
            type="checkbox"
            checked={confirmed}
            onChange={(e) => {
              setConfirmed(e.target.checked);

              if (e.target.checked) {
                setError('');
              }
            }}
            className="mt-1 w-4 h-4 rounded border-gray-300"
          />

          <span className="text-sm text-gray-600 leading-6">
            <span className="text-red-500" aria-hidden="true">
              *
            </span>{' '}
            I confirm that the details, isolations, and earths specified in this application are accurate and ready for operational review.
          </span>
        </label>

        {error && <div className="mt-2 text-sm text-red-500">{error}</div>}
      </div>
    </div>
  );
});

Signature.displayName = 'Signature';

export default Signature;
